import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
} from "@mui/material";
import {
  CleaningServices as DeepCleanIcon,
  Window as BalconyIcon,
  Handyman as HandymanIcon,
  PhotoCamera as PhotoshootIcon,
  CalendarMonth as SeasonalIcon,
  ShoppingCart as RestockingIcon,
} from "@mui/icons-material";
import SectionHeader from "../components/SectionHeader";

interface ServicesGridProps {
  bgcolor?: string;
  onGetQuote?: (serviceType: string) => void;
}

const services = [
    {
        icon: (
          <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(255, 56, 92, 0.08)" }}>
            <DeepCleanIcon sx={{ fontSize: 36, color: "#FF385C" }} />
          </Box>
        ),
        title: "Deep Cleaning",
        description: "Thorough cleaning after long stays or heavy use. Greater depth on buildup, cabinets, baseboards, and detailed bathroom and kitchen areas",
        serviceType: "deep",
    },
    {
        icon: (
          <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(3, 169, 244, 0.08)" }}>
            <BalconyIcon sx={{ fontSize: 36, color: "#03A9F4" }} />
          </Box>
        ),
        title: "Balcony & Window Cleaning",
        description: "Professional balcony refresh and window cleaning. Includes glass, frames, railings, sweeping, and surface cleaning",
        serviceType: "balcony",
    },
    {
        icon: (
          <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(255, 152, 0, 0.08)" }}>
            <HandymanIcon sx={{ fontSize: 36, color: "#FF9800" }} />
          </Box>
        ),
        title: "Handyman Services",
        description: "Minor repairs and maintenance for guest readiness. Includes loose handles, light adjustments, and basic furniture assembly",
        serviceType: "handyman",
    },
    {
      icon: (
        <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(103, 58, 183, 0.08)" }}>
          <RestockingIcon sx={{ fontSize: 36, color: "#673AB7" }} />
        </Box>
      ),
      title: "Restocking & Supply Management",
      description: "We purchase approved supplies, restock your property, and manage inventory to ensure essential guest amenities are always available",
      serviceType: "restocking",
    },
    {
      icon: (
        <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(156, 39, 176, 0.08)" }}>
          <SeasonalIcon sx={{ fontSize: 36, color: "#9C27B0" }} />
        </Box>
      ),
      title: "Seasonal Property Refresh",
      description: "Periodic maintenance cleaning that covers areas not typically included in regular turnovers, helping keep your property in excellent condition year-round",
      serviceType: "seasonal",
    },
    {
        icon: (
          <Box sx={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, bgcolor: "rgba(255, 87, 34, 0.08)" }}>
            <PhotoshootIcon sx={{ fontSize: 36, color: "#FF5722" }} />
          </Box>
        ),
        title: "Photoshoot Preparation",
        description: "Make a great first impression with professionally prepared listing photos. We clean, stage, and present your property to help showcase it at its best",
        serviceType: "photoshoot",
    },


];

export default function ServicesGrid({ bgcolor = "background.default", onGetQuote }: ServicesGridProps) {
  const handleGetQuote = (serviceType: string) => {
    if (onGetQuote) {
      onGetQuote(serviceType);
    }
  };

  return (
    <Box sx={{ width: "100vw", bgcolor }}>
      <Container
        component="section"
        maxWidth="lg"
        sx={{
          maxWidth: "1200px !important",
          py: { xs: 4, md: 7 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <SectionHeader
          title="Additional Services"
          subtitle="Not included in BNB Cleaning packages. Quoted separately."
        />

        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  bgcolor: "background.default",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                  }}
                >
                  <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>{service.icon}</Box>
                    
                    <Typography variant="h5" fontWeight="bold">
                      {service.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ flexGrow: 1, lineHeight: 1.6 }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleGetQuote(service.serviceType)}
                      sx={{ mt: 2, alignSelf: "center" }}
                    >
                      Get Quote
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
